// CoPilot, Claude side (CP-3, Specs/CoPilot — Claude Code + LangGraph — PRD). The server (the Launch
// OS connector, /copilot/turn) streams events already translated into the panel's message shape;
// this keeps the running state and hands the panel the same `messages` list LangGraph's useStream
// gives it. Status steps and Approve cards ride the panel's existing artifact registry:
//   - each tool call is renamed to STEP_ARTIFACT (a "tool_call" artifact) with { label, status }
//   - each Approve request becomes a message whose content is { artifactId: APPROVAL_ARTIFACT, … }

export const STEP_ARTIFACT = "copilot_step";
export const APPROVAL_ARTIFACT = "copilot_approval";

export interface PanelMessage {
  type: "human" | "ai" | "tool";
  id: string;
  content: string;
  tool_calls?: { id: string; name: string; args: Record<string, unknown> }[];
  tool_call_id?: string;
  name?: string;
}
interface Step { label: string; status: "running" | "done" | "failed" }
interface Approval { id: string; summary: string; status: "waiting" | "approved" | "cancelled"; afterId: string | null }
export interface CopilotState {
  order: string[];
  byId: Record<string, PanelMessage>;
  steps: Record<string, Step>;
  approvals: Approval[];
  sessionId: string | null;
  isLoading: boolean;
  errors: number;
}

export const emptyCopilotState = (): CopilotState => ({ order: [], byId: {}, steps: {}, approvals: [], sessionId: null, isLoading: false, errors: 0 });

const upsert = (s: CopilotState, m: PanelMessage): CopilotState => ({
  ...s,
  order: s.byId[m.id] ? s.order : [...s.order, m.id],
  byId: { ...s.byId, [m.id]: m },
});

export const applyCopilotEvent = (s: CopilotState, ev: any): CopilotState => {
  switch (ev && ev.event) {
    case "start":
      return { ...s, isLoading: true };
    case "human":
      return upsert({ ...s, isLoading: true }, ev.message);
    case "session":
      return { ...s, sessionId: ev.sessionId };
    case "upsert":
      return upsert(s, ev.message);
    case "step":
      return { ...s, steps: { ...s.steps, [ev.step.id]: { label: ev.step.label, status: ev.step.status } } };
    case "approval":
      return { ...s, approvals: [...s.approvals, { id: ev.approval.id, summary: ev.approval.summary, status: "waiting", afterId: s.order[s.order.length - 1] || null }] };
    case "approval_result":
      return { ...s, approvals: s.approvals.map((a) => (a.id === ev.id ? { ...a, status: ev.approved ? "approved" : "cancelled" } : a)) };
    case "done": {
      const next = { ...s, isLoading: false, sessionId: ev.sessionId || s.sessionId };
      if (!ev.error) return next;
      const errors = s.errors + 1;
      return upsert({ ...next, errors }, { type: "ai", id: `error-${errors}`, content: ev.error, tool_calls: [] });
    }
    default:
      return s;
  }
};

// The messages the panel shows: tool calls become status-step cards, raw tool results are blanked
// (the card already says done), and Approve cards sit right after the message they follow.
export const panelMessages = (s: CopilotState): PanelMessage[] => {
  const out: PanelMessage[] = [];
  const cards = (afterId: string | null) =>
    s.approvals
      .filter((a) => a.afterId === afterId)
      .map((a): PanelMessage => ({ type: "ai", id: `approval-${a.id}`, content: JSON.stringify({ artifactId: APPROVAL_ARTIFACT, id: a.id, summary: a.summary, status: a.status }), tool_calls: [] }));
  out.push(...cards(null));
  let lastLabel: string | null = null; // the same step several times in a row shows once
  for (const id of s.order) {
    const m = s.byId[id];
    if (m.type === "ai" && m.tool_calls && m.tool_calls.length) {
      const calls = m.tool_calls
        .map((c) => {
          const step = s.steps[c.id] || { label: "Working…", status: "running" };
          return { id: c.id, name: STEP_ARTIFACT, args: { label: step.label, status: step.status } };
        })
        .filter((c) => {
          const repeat = c.args.label === lastLabel;
          lastLabel = c.args.label;
          return !repeat;
        });
      if (!calls.length && !m.content) continue;
      out.push({ ...m, tool_calls: calls });
    } else if (m.type === "tool") {
      // (tool results don't break a run of identical steps)
      out.push({ ...m, name: STEP_ARTIFACT, content: "" });
    } else {
      if (m.content) lastLabel = null;
      out.push(m);
    }
    out.push(...cards(id));
  }
  return out;
};

// Server-sent events: "data: {json}\n\n" chunks; ": …" lines are keep-alives.
export const parseSse = (buffer: string) => {
  const parts = buffer.split("\n\n");
  const rest = parts.pop() || "";
  const events = parts
    .map((p) => p.split("\n").filter((l) => l.startsWith("data: ")).map((l) => l.slice(6)).join(""))
    .filter(Boolean)
    .map((j) => {
      try {
        return JSON.parse(j);
      } catch {
        return null;
      }
    })
    .filter(Boolean);
  return { events, rest };
};
